import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { answerOptionIntakeFormQuestion } from '@api';
import {
  Checkbox,
  ErrorMessage,
  Field,
  Label,
  Radio,
  RadioGroup,
} from '@quickmd-llc/design-system';
import { OptionQuestion } from '@qmd-types/intakeForms';
import { SubmitButton } from './SubmitButton';
import { useIntakeForm } from '../../context/useIntakeForm';

type Props = {
  question: OptionQuestion;
};

type FormValues = {
  options: string[];
};

function getDefaultValues(question: OptionQuestion): FormValues {
  const options = question.options
    .filter((option) => option.isSelected)
    .map((option) => option.name);

  return {
    options,
  };
}

export function OptionForm({ question }: Props) {
  const { isMultipleAnswerAllowed, options, question: questionText } = question;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleNextStep, intakeForm, updateAnswer } = useIntakeForm();
  const { control, handleSubmit, formState, setValue } = useForm<FormValues>({
    defaultValues: getDefaultValues(question),
  });
  const { errors } = formState;
  const hasError = Boolean(errors.options);

  const rules = {
    required: {
      value: question.isRequired,
      message: isMultipleAnswerAllowed
        ? 'Please select one'
        : 'Please select an option',
    },
  };

  async function onSubmit(data: FormValues) {
    const { intakeFormQuestionId } = question;
    const { intakeFormId } = intakeForm;

    setIsLoading(true);

    try {
      await answerOptionIntakeFormQuestion({
        intakeFormQuestionId,
        intakeFormId,
        data,
      });

      const updatedOptions = options.map((option) => ({
        name: option.name,
        isSelected: data.options.includes(option.name),
      }));

      const updatedQuestion: OptionQuestion = {
        ...question,
        options: updatedOptions,
      };

      updateAnswer(intakeFormQuestionId, updatedQuestion);
      handleNextStep();
    } catch {
      // @TODO: Handle error
      setIsLoading(false);
    }
  }

  function handleRadioChange(value: string) {
    setValue('options', [value]);
  }

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
      <Field className="mb-2" error={hasError}>
        <Label>{questionText}</Label>
        {hasError && <ErrorMessage>{errors.options?.message}</ErrorMessage>}
      </Field>

      {isMultipleAnswerAllowed && (
        <Controller
          control={control}
          name="options"
          render={({
            field: { onChange, value },
            formState: { defaultValues },
          }) => {
            function handleChange(name: string, isChecked: boolean) {
              const values = new Set(value);

              if (isChecked) {
                values.add(name);
              } else {
                values.delete(name);
              }

              onChange(Array.from(values));
            }

            return (
              <div className="grid gap-sp-1">
                {options.map((option) => (
                  <Field key={option.name} className="flex items-center">
                    <Checkbox
                      name={option.name}
                      value={option.name}
                      onChange={(isChecked) =>
                        handleChange(option.name, isChecked)
                      }
                      defaultChecked={defaultValues?.options?.includes(
                        option.name,
                      )}
                    />
                    <Label>{option.name}</Label>
                  </Field>
                ))}
              </div>
            );
          }}
          rules={rules}
        />
      )}

      {!isMultipleAnswerAllowed && (
        <Controller
          control={control}
          name="options"
          render={({ formState: { defaultValues } }) => {
            const defaultValue = defaultValues?.options?.[0];

            return (
              <RadioGroup
                defaultValue={defaultValue}
                onChange={handleRadioChange}
                className="grid gap-sp-1"
              >
                {options.map((option) => (
                  <Field key={option.name}>
                    <Radio value={option.name} />
                    <Label>{option.name}</Label>
                  </Field>
                ))}
              </RadioGroup>
            );
          }}
          rules={rules}
        />
      )}

      <SubmitButton isLoading={isLoading} />
    </form>
  );
}
